import React, { useCallback, useEffect, useRef, useState } from 'react';
import { checkRetrievalRecovery, retryRetrieval, retryRetrievalWarning, deleteServerCopy, deleteWarning, downloadRecording, resolveFailedRetrieval, resolveFailedWarning, retrievalError, retrievalHistory, retrievalSize, retrievalStatus } from './videoRetrievalApi';
import './video.css';

export default function VideoRetrievalHistory() {
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [selectionMessage, setSelectionMessage] = useState('');
  const [selectedId] = useState(() => {
    const id = new URLSearchParams(window.location.search).get('recording');
    return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : null;
  });
  const searchTerms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visibleRows = rows.filter(row => {
    if ((channel && String(row.channel) !== channel) || (status && row.status !== status)) return false;
    const text = [row.assetName, row.environment, `CH${row.channel}`, row.requestedBy,
      row.recordingStart, row.recordingEnd, row.status].join(' ').toLowerCase();
    return searchTerms.every(term => text.includes(term));
  });
  const channels = [...new Set(rows.map(row => row.channel))].sort((a, b) => a - b);
  const statuses = [...new Set(rows.map(row => row.status))].sort();
  const filtered = !!(search || channel || status);
  const clearFilters = () => { setSearch(''); setChannel(''); setStatus(''); };
  const selectedRow = useRef(null);
  const focusedSelection = useRef(false);
  const mounted = useRef(false);
  const refreshing = useRef(false);
  const actionPending = useRef(false);
  const refresh = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    if (mounted.current) setLoading(true);
    try {
      let data = await retrievalHistory();
      let selectionError = '';
      // History contains only the latest jobs; the authorised single-job endpoint also
      // lets an older selected recording remain visible without expanding everyone's history.
      if (selectedId && !data.some(row => row.id === selectedId)) {
        try { data = [await retrievalStatus(selectedId), ...data]; }
        catch { selectionError = 'The selected recording could not be loaded. It may no longer be available to your account.'; }
      }
      if (mounted.current) { setRows(data); setMessage(''); setSelectionMessage(selectionError); setUpdated(new Date()); }
    }
    catch (error) { if (mounted.current) setMessage(retrievalError(error)); }
    finally { refreshing.current = false; if (mounted.current) setLoading(false); }
  }, [selectedId]);
  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = setInterval(() => { if (!document.hidden) refresh(); }, 10000);
    return () => { mounted.current = false; clearInterval(timer); };
  }, [refresh]);
  useEffect(() => {
    if (!selectedId || focusedSelection.current || !selectedRow.current) return;
    selectedRow.current.scrollIntoView?.({ block: 'center', behavior: 'auto' });
    selectedRow.current.focus({ preventScroll: true });
    focusedSelection.current = true;
  }, [rows, selectedId, search, channel, status]);
  const action = async (row, deleting) => {
    if (actionPending.current || (deleting && !window.confirm(deleteWarning))) return;
    actionPending.current = true; setBusy(row.id); setMessage('');
    try {
      if (deleting) await deleteServerCopy(row.id); else await downloadRecording(row.id);
      await refresh();
    } catch (error) { if (mounted.current) setMessage(retrievalError(error)); }
    finally { actionPending.current = false; if (mounted.current) setBusy(''); }
  };
  const resolveFailed = async row => {
    if (actionPending.current || !window.confirm(resolveFailedWarning(row))) return;
    actionPending.current = true; setBusy(row.id); setMessage(''); setActionNotice('');
    try {
      const current = await retrievalStatus(row.id);
      if (!current.canResolveFailed) {
        await refresh();
        if (mounted.current) setMessage('This retrieval is no longer eligible. Its status has been refreshed.');
        return;
      }
      await resolveFailedRetrieval(row.id, current.version);
      await refresh();
      if (mounted.current) setActionNotice('Retrieval resolved as failed. Its camera lock has been released.');
    } catch (error) {
      if (mounted.current) setMessage(retrievalError(error));
    } finally { actionPending.current = false; if (mounted.current) setBusy(''); }
  };
  const recover = async (row, retry) => {
    if (actionPending.current || (retry && !window.confirm(retryRetrievalWarning(row)))) return;
    actionPending.current = true; setBusy(row.id); setMessage(''); setActionNotice('');
    try {
      const current = await retrievalStatus(row.id);
      if (!(retry ? current.canRetry : current.canCheckRecovery)) {
        await refresh();
        if (mounted.current) setMessage('This retrieval has changed. Its status has been refreshed.');
        return;
      }
      const result = retry ? await retryRetrieval(row.id, current.version) : await checkRetrievalRecovery(row.id, current.version);
      await refresh();
      if (mounted.current) setActionNotice(retry ? 'Upload retry queued. Existing provider activity or a copy will be checked before a new camera command.' :
        result.status === 'Ready' ? 'Server copy confirmed. The recording is ready to download.' :
        result.status === 'Uploading' ? 'The provider reports an active upload. Monitoring continues.' :
        result.status === 'Processing' ? 'A server copy was found. Monitoring is checking that its size is stable before download.' :
        result.failureReason || 'Provider check completed. Review the updated status before resolving this upload.');
    } catch (error) { if (mounted.current) setMessage(retrievalError(error)); }
    finally { actionPending.current = false; if (mounted.current) setBusy(''); }
  };
  return <main className="video-retrieval-history container-fluid">
    <div className="video-retrieval-header">
      <h2>Retrieved recordings</h2>
      <details className="video-retrieval-help">
        <summary>About retrievals</summary>
        <p>Recordings remain on the video server until explicitly deleted or removed by provider cleanup. Times shown for recordings are camera local time. Closing this page does not cancel retrieval.</p>
        <p>For an interrupted upload, Check upload and Resolve as failed become available after 30 minutes. Check upload looks for an active transfer or completed copy. Resolve as failed releases the camera only after a fresh provider check confirms no active upload or matching copy. Failed requests offer Retry upload. Recovery actions are available in the environment where the request was created.</p>
      </details>
      <div className="video-retrieval-toolbar">
        <button type="button" className="btn btn-outline-primary" onClick={refresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        <span role="status">{loading ? 'Checking recording status…' : updated ? `Updated at ${updated.toLocaleTimeString()}` : ''}</span>
      </div>
    </div>
    <div className="video-retrieval-filters" role="search" aria-label="Filter retrieved recordings">
      <label>Search recordings<input type="search" className="form-control" placeholder="Vehicle, recording date, channel or requester" value={search} onChange={event => setSearch(event.target.value)} /></label>
      <label>Channel<select className="form-control" value={channel} onChange={event => setChannel(event.target.value)}><option value="">All channels</option>{channels.map(value => <option key={value} value={value}>CH{value}</option>)}</select></label>
      <label>Status<select className="form-control" value={status} onChange={event => setStatus(event.target.value)}><option value="">All statuses</option>{statuses.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <button type="button" className="btn btn-outline-secondary" onClick={clearFilters} disabled={!filtered}>Clear filters</button>
      <span role="status">Showing {visibleRows.length} of {rows.length} loaded recordings</span>
    </div>
    {selectedId && !selectionMessage && rows.some(row => row.id === selectedId) && <p className="video-retrieval-selection-notice">{visibleRows.some(row => row.id === selectedId) ? 'The selected recording is highlighted below.' : 'The selected recording is hidden by your filters. Clear filters to show it.'}</p>}
    {selectionMessage && <p role="alert">{selectionMessage}</p>}
    {message && <p role="alert">{message}</p>}
    {actionNotice && <p role="status">{actionNotice}</p>}
    <div className="table-responsive"><table className="table"><thead><tr>
      <th>Vehicle</th><th>Recording</th><th>Channel</th><th>Requested by</th><th>Requested time</th><th>Status</th><th>Progress</th><th>Size</th><th>Actions</th>
    </tr></thead><tbody>{visibleRows.map(row => <tr key={row.id} className={[row.id === selectedId && 'video-retrieval-selected', row.status === 'Failed' && 'video-retrieval-failed'].filter(Boolean).join(' ') || undefined} tabIndex={row.id === selectedId ? -1 : undefined} ref={row.id === selectedId ? selectedRow : null}>
      <td>{row.id === selectedId && <strong className="video-retrieval-selected-label">Selected recording</strong>}{row.assetName}<small className="d-block">{row.environment}</small></td>
      <td>{row.recordingStart}<br />{row.recordingEnd}</td><td>CH{row.channel}</td>
      <td>{row.requestedBy}</td><td>{new Date(row.requestedUtc.endsWith('Z') ? row.requestedUtc : `${row.requestedUtc}Z`).toLocaleString()}</td>
      <td><span className={row.status === 'Failed' ? 'video-retrieval-failed-status' : undefined}>{row.status}</span>{row.failureReason && <small className="d-block">{row.failureReason}</small>}{row.recoveryHint && <small className="d-block">{row.recoveryHint}</small>}</td>
      <td>{row.progressPercent == null ? '—' : `${row.progressPercent}%`}</td>
      <td>{retrievalSize(row.actualBytes ?? row.estimatedBytes)}{row.actualBytes == null && <small className="d-block">Estimate</small>}</td>
      <td>{row.canDownload && <button type="button" className="btn btn-primary btn-sm mr-2" disabled={!!busy} onClick={() => action(row, false)}>Download</button>}
        {row.canDelete && <button type="button" className="btn btn-outline-danger btn-sm" disabled={!!busy} onClick={() => action(row, true)}>Delete</button>}
        {row.canCheckRecovery && <button type="button" className="btn btn-outline-primary btn-sm mr-2" disabled={!!busy} onClick={() => recover(row, false)}>Check upload</button>}
        {row.canRetry && <button type="button" className="btn btn-outline-primary btn-sm mr-2" disabled={!!busy} onClick={() => recover(row, true)}>Retry upload</button>}
        {row.canResolveFailed && <button type="button" className="btn btn-outline-warning btn-sm" disabled={!!busy} onClick={() => resolveFailed(row)}>Resolve as failed</button>}</td>
    </tr>)}</tbody></table></div>
    {!loading && !rows.length && !message && <p>No retrieved recordings.</p>}
    {!!rows.length && !visibleRows.length && <p>No recordings match your filters.</p>}
  </main>;
}
